import { IllegalMapOperationError } from '../../../lib/custom_errors';
import { Node, Link } from '../store';
import CopyPaste from 'lib/copy-paste';
import { nui } from 'lib/nodes-ui';
import { updateSelectedNodeTree, refreshFreehandAncestorsOfList } from 'lib/util.js';

class SystemsUI {

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  createSystemFromSelection() {
    const { canvas } = this;

    try {
      const { scale, tx, ty } = canvas.getMatrix();

      let topX = (window.innerWidth - tx) / scale;
      let topY = (window.innerHeight - ty) / scale;
      let topXrelative = topX;
      let topYrelative = topY;

      let selectedNodes = canvas.getRootSelectedNodes();

     /*
      * also get the top-left x,y coordinate of the selected area
      */
      selectedNodes.forEach(node => {
        const absolutePosition = node.absolutePosition;
        if (absolutePosition.x < topX) {
          topX = absolutePosition.x;
          topXrelative = node.x;
        }
        if (absolutePosition.y < topY) {
          topY = absolutePosition.y;
          topYrelative = node.y;
        }
      });

      /*
       * get selected nodes and selected link ids
       * as well as current positions & link angles to use in the undo event
       */
      const targetNodeIds = selectedNodes.map(n => n.id );
      const targetLinkIds = canvas.selectedElements.filter(e => {
        return(e.instance instanceof Link);
      }).map(e => { return e.instance.id; });
      const prevNodePositions = store.getNodePositions(targetNodeIds);
      const prevLinkAngles = store.getLinkAngles(targetLinkIds);


      let newNodeLayout = 'layout-freehand';

      // if we selected nodes under one or more parents,
      // and all those parents have the same layout, then use that layout
      // otherwise, use freehand
      if (selectedNodes.length && selectedNodes.filter(n => n.parentId).every(n => n.getGoverningLayoutName() === selectedNodes[0].getGoverningLayoutName())) {
        if (selectedNodes[0].getGoverningLayoutName()) {
          newNodeLayout = selectedNodes[0].getGoverningLayoutName();
        }
      }

      /*
       * create new parent node
       */
      const nodeId = canvas.store.addNode({
        x: topX,
        y: topY,
        title: '',
        summary: "",
        createdById: window.user_id,
        layoutName: newNodeLayout,
        orderIndex: 0,
      });
      const newNode = self.store.getNode(nodeId);

      /*
       * determine how many distinct parents there are among all selected nodes & how many are top level nodes
       */
      const classification = canvas.classifySelection(selectedNodes);

      let i = 0;
      let oldParents = [];
      /*
       * if selected nodes are under one parent, create the new node under that parent and put all children into it
       */
      if (classification === 'shared-parent') {
        // create a new whole for children under one common parent
        const parent = store.getNode(selectedNodes[0].parentId);

        if (parent.getLayoutName() == 'layout-freehand') {
          // normalize positions are nodes inside freehand layout so they don't end up way off the canvas in their new parent
          CopyPaste.normalizeNodePositions({nodes: selectedNodes});
          newNode.x = topXrelative;
          newNode.y = topYrelative;
        }

        selectedNodes.forEach(node => {
          if (!node.link()) {
            node.absolutePosition.x = node.x + parent.x;
            node.absolutePosition.y = node.y + parent.y;
            node.adoptParent(nodeId);
            node.setOrderIndex(i++);
          }
        });
        // make the new node a child of the form parent & use same layout as parent
        newNode.adoptParent(parent.id);
        newNode.setLayoutName( parent.getLayoutName() );
      } else if (selectedNodes.length === 1) {
        const node = selectedNodes[0];
        if (!node.link()) {
          node.x = 0;
          node.y = 0;
          node.adoptParent(nodeId);
        } else {
          return;
        }
      } else {
      /*
       * If selected nodes are all top level nodes or spread across multiple systems,
       * grab them out into a new top level system
       */

        CopyPaste.normalizeNodePositions({nodes: selectedNodes.filter(n => n.getGoverningLayoutName() != 'layout-freehand' && !n.parentId)});

        let incrementX = 0;
        let incrementY = 0;
        let height = 40; // min height in case we don't have it defined for a node

        // adopt the new node into the common ancestor of the selected nodes, if it's not the top-level
        const newNodeParent = store.commonAncestor(selectedNodes);
        if (newNodeParent) {
          // adjust the positioning of the new node
          newNode.x -= newNodeParent.absolutePosition.x;
          newNode.y -= newNodeParent.absolutePosition.y;
          newNode.adoptParent(newNodeParent.id);
        }

        selectedNodes.forEach(node => {
          if (node.parentId) {
            oldParents.push(node.parentId);
          }
          if (!node.link()) {
            if (newNode.getLayoutName() == 'layout-freehand') {
              // position nodes in a grid in their new parent - if pulling nodes from multiple parents
              if (classification === 'heterogeneous') {
                node.x = incrementX;
                node.y = incrementY;
                incrementX += node.width + 20;
                height = Math.max(height,node.height);
                // set a reasonable width to wrap our card grid on...
                if (incrementX > Math.max(window.innerWidth / 2, 400)) {
                  incrementX = 0;
                  incrementY += height + 20;
                  height = 40;
                }
              }
            }
            node.adoptParent(nodeId);
            node.setOrderIndex(i++);
          }
        });
      }
      // unique array of parent ids
      oldParents = oldParents.filter((value, index, arr) => {
        return arr.indexOf(value) === index;
      });
      // clear our selections (but don't trigger style updates)
      canvas.clearSelectedElements();

      // refresh all positions & sizes
      const ids = selectedNodes.map(n => n.id);
      ids.push(newNode.id);
      updateSelectedNodeTree(ids,canvas);
      refreshFreehandAncestorsOfList(ids,canvas,store);
      if (oldParents.length) {
        // resize any old parents that we pulled children from
        updateSelectedNodeTree(oldParents,canvas);
        refreshFreehandAncestorsOfList(oldParents,canvas,store);
      }
      const batchMessages = [
        canvas.operation.messages.addNode({
          node: newNode
        }),
        canvas.operation.messages.updateNodePositions({
          prevNodePositions,
          prevLinkAngles,
          nodePositions: store.getNodePositions(targetNodeIds),
          linkAngles: store.getLinkAngles(targetLinkIds),
          selectedNodes: selectedNodes.map(n => n.id),
        }),
      ];

      const newNodeTag = canvas.nodeTags[nodeId];
      pui.addNodeToActivePerspectives(newNodeTag, batchMessages);

      canvas.operation.batchOperation({
        messages: batchMessages,
        onFailure: (err) => {
          console.log(`createSystemFromSelection batchOperation failure: ${err}`);
        },
      });
      canvas.setSelectedElement(newNodeTag);
      canvas.bulkActionsMenuTag.update();
      appUI.zoomToCardIfZoomedOut(newNodeTag);
      canvas.waypointsTabTag.refreshIfNeeded();
      newNodeTag.focusTitle(true, false);

      nui.fixOverlaps({ nodeTag: newNodeTag });

      nui.fitCardsIntoFreehand({
        nodeTags: selectedNodes.map(({ id }) => canvas.getNodeTag({ id })),
      });

    } catch (e) {
      if (e instanceof IllegalMapOperationError) {
        // swallow exception
      } else {
        throw e;
      }
    }
  };
}

// singleton instance
const systemsUI = new SystemsUI();

export { SystemsUI, systemsUI };



// WEBPACK FOOTER //
// ./starling/src/lib/systems-ui.js